import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mountain, LogOut, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { toast } from "sonner";

const TABS = ["Orders", "Enquiries", "Price Matches", "Rider Stories", "Inventory", "Waitlist", "Exports", "Go Live"];

const STRIPE_CLAIM_URL = "";

const GO_LIVE_STEPS = [
  {
    key: "insurance",
    title: "Confirm business insurance",
    body: "The store goes live the moment cover is confirmed. Nothing in the code blocks this — it's the last business-side gate.",
    href: null,
    cta: null,
  },
  {
    key: "deploy",
    title: "Deploy from Emergent",
    body: "The automated readiness check passed with zero blockers. Deploy is one click from the Emergent interface.",
    href: null,
    cta: null,
  },
  {
    key: "smoke",
    title: "Enquiry smoke test",
    body: "Send one enquiry from any bike page and one from the contact form — confirm both land at ecobikecampsupp@gmail.com within a minute.",
    href: null,
    cta: null,
  },
  {
    key: "deals",
    title: "Verify the deals are current",
    body: "The bundle offers are editable placeholders — check they're what you actually want to honour before traffic arrives.",
    href: null,
    cta: null,
  },
  {
    key: "housekeeping",
    title: "Housekeeping",
    body: "Change the admin password (ask Neo to reseed) and connect Instagram @1manameis properly when ready.",
    href: null,
    cta: null,
  },
];

const inputCls =
  "w-full border border-brass/20 bg-forest-deep/70 px-4 py-3 text-sm text-bone placeholder:text-sand/30 outline-none transition-colors focus:border-brass";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const fmtMoney = (amount, currency) =>
  new Intl.NumberFormat(currency === "GBP" ? "en-GB" : "de-DE", {
    style: "currency", currency: currency || "GBP", maximumFractionDigits: 0,
  }).format(amount || 0);

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const Panel = ({ title, children, testId }) => (
  <div data-testid={testId} className="border border-brass/15 bg-charcoal/60">
    <div className="border-b border-brass/15 px-5 py-3">
      <p className="font-mono text-[10px] tracking-[0.22em] text-brass uppercase">{title}</p>
    </div>
    <div className="divide-y divide-brass/10">{children}</div>
  </div>
);

const Empty = ({ children }) => (
  <p className="px-5 py-8 text-center text-sm font-light text-sand/40">{children}</p>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("xs-admin-token") || "");
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [tab, setTab] = useState("Orders");
  const [overview, setOverview] = useState(null);
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [priceMatches, setPriceMatches] = useState([]);
  const [stories, setStories] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockEdits, setStockEdits] = useState({});
  const [dispatchFor, setDispatchFor] = useState(null);
  const [tracking, setTracking] = useState({ tracking_number: "", courier: "" });
  const [noteDrafts, setNoteDrafts] = useState({});
  const [liveChecks, setLiveChecks] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("xs-golive") || "{}");
    } catch {
      return {};
    }
  });
  const [waitlist, setWaitlist] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const authed = useCallback(
    (path) => api.get(path, { headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const loadAll = useCallback(async () => {
    setLoadingData(true);
    try {
      const [me, ov, o, e, pm, rs, pr, wl] = await Promise.all([
        authed("/auth/me"),
        authed("/admin/overview"),
        authed("/admin/orders"),
        authed("/admin/enquiries"),
        authed("/admin/price-matches"),
        authed("/admin/rider-stories"),
        api.get("/products"),
        authed("/admin/waitlist"),
      ]);
      setUser(me.data);
      setOverview(ov.data);
      setOrders(o.data);
      setEnquiries(e.data);
      setPriceMatches(pm.data);
      setStories(rs.data);
      setProducts(pr.data);
      setWaitlist(wl.data);
    } catch {
      setUser(null);
      setToken("");
      localStorage.removeItem("xs-admin-token");
    } finally {
      setLoadingData(false);
    }
  }, [authed]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", loginForm);
      localStorage.setItem("xs-admin-token", data.token);
      setToken(data.token);
      setUser(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/rider-stories/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setStories((prev) => prev.map((s) => (s.id === id ? { ...s, status: "approved" } : s)));
      toast.success("Story approved");
    } catch {
      toast.error("Could not approve");
    }
  };

  const advanceStatus = async (orderId, status, extra = {}) => {
    try {
      const { data } = await api.post(
        `/admin/orders/${orderId}/status`,
        { status, ...extra },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: data.status, ...extra } : o))
      );
      setDispatchFor(null);
      toast.success(`Order ${orderId} marked ${data.status} — customer emailed`);
    } catch {
      toast.error("Could not update order");
    }
  };

  const refundOrder = async (orderId) => {
    if (!window.confirm("Refund/cancel this order? Stock will be restored.")) return;
    try {
      const { data } = await api.post(
        `/admin/orders/${orderId}/refund`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: data.status, payment_status: data.status } : o))
      );
      toast.success(`Order ${data.status} — customer emailed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveStock = async (productId) => {
    const stock = parseInt(stockEdits[productId], 10);
    if (Number.isNaN(stock) || stock < 0) {
      toast.error("Enter a valid stock number");
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/products/${productId}/stock`,
        { stock },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProducts((prev) => prev.map((p) => (p.id === productId ? { ...p, stock: data.stock } : p)));
      toast.success(
        data.waitlist_notified
          ? `Stock set — ${data.waitlist_notified} waitlist rider(s) emailed`
          : "Stock updated"
      );
    } catch {
      toast.error("Could not update stock");
    }
  };

  const sendDigest = async () => {
    try {
      await api.post("/admin/digest", {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Digest sent to the owner inbox");
    } catch {
      toast.error("Could not send digest");
    }
  };

  const saveNote = async (orderId) => {
    const note = (noteDrafts[orderId] || "").trim();
    if (!note) return;
    try {
      const { data } = await api.post(
        `/admin/orders/${orderId}/notes`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, notes: [...(o.notes || []), data.note] } : o))
      );
      setNoteDrafts((d) => ({ ...d, [orderId]: "" }));
      toast.success("Note saved");
    } catch {
      toast.error("Could not save note");
    }
  };

  const nudgeOrder = async (orderId) => {
    try {
      await api.post(`/admin/orders/${orderId}/nudge`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, reminder_sent: true } : o)));
      toast.success("Reminder emailed to the rider");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async (dataset) => {
    try {
      const { data } = await api.get(`/admin/export/${dataset}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `offroad-electric-bikes-${dataset}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const decidePriceMatch = async (id, decision) => {
    try {
      await api.post(`/admin/price-matches/${id}/decision`, { decision }, { headers: { Authorization: `Bearer ${token}` } });
      setPriceMatches((prev) => prev.map((p) => (p.id === id ? { ...p, status: decision } : p)));
      toast.success(`Price match ${decision} — customer emailed`);
    } catch {
      toast.error("Could not update request");
    }
  };

  if (!token || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest-deep px-4 text-bone">
        <div className="grain-overlay" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="admin-login-card"
          className="w-full max-w-sm border border-brass/30 bg-charcoal p-8"
        >
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center border border-brass/50 bg-forest-emerald/60">
              <Mountain className="h-4 w-4 text-brass" strokeWidth={1.75} />
            </span>
            <span className="font-serif text-xs font-bold tracking-[0.18em]">
              OFF-ROAD ELECTRIC BIKES
            </span>
          </Link>
          <h1 className="mt-8 font-serif text-3xl font-semibold">Console Login</h1>
          <p className="mt-2 text-sm font-light text-sand/55">Staff only.</p>
          <form data-testid="admin-login-form" onSubmit={login} className="mt-6 space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              placeholder="Email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              placeholder="Password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
            />
            {error && (
              <p data-testid="admin-login-error" className="font-mono text-xs text-[#E65100]">{error}</p>
            )}
            <button
              data-testid="admin-login-submit"
              className="clip-corner-sm w-full bg-brass py-3.5 font-mono text-[11px] font-bold tracking-[0.22em] text-forest-deep uppercase transition-colors hover:bg-brass-bright"
            >
              Enter the Depot
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forest-deep text-bone">
      <div className="grain-overlay" />
      <header className="sticky top-0 z-40 border-b border-brass/20 bg-forest-deep/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center border border-brass/50 bg-forest-emerald/60">
              <Mountain className="h-4 w-4 text-brass" strokeWidth={1.75} />
            </span>
            <div>
              <p className="font-serif text-base font-bold tracking-[0.18em]">OFF-ROAD ELECTRIC BIKES</p>
              <p className="font-mono text-[9px] tracking-[0.2em] text-sand/40 uppercase">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-digest-button"
              onClick={sendDigest}
              className="border border-brass/25 px-3 py-2 font-mono text-[10px] tracking-[0.18em] text-sand/60 uppercase transition-colors hover:border-brass hover:text-brass"
            >
              Digest
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={loadAll}
              className="border border-brass/25 p-2 text-sand/60 transition-colors hover:border-brass hover:text-brass"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loadingData ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                localStorage.removeItem("xs-admin-token");
                setToken("");
                setUser(null);
              }}
              className="flex items-center gap-2 border border-brass/25 px-3 py-2 font-mono text-[10px] tracking-[0.18em] text-sand/60 uppercase transition-colors hover:border-brass hover:text-brass"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        {overview && (
          <div data-testid="admin-overview" className="grid grid-cols-2 gap-px border border-brass/15 bg-brass/10 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Orders", overview.orders],
              ["Enquiries", overview.enquiries],
              ["Price Matches", overview.price_matches],
              ["Stories Pending", overview.rider_stories_pending],
              ["Newsletter Members", overview.newsletter],
              ["On Waitlists", overview.waitlist],
            ].map(([label, value]) => (
              <div key={label} className="bg-forest-deep/90 p-5">
                <p className="font-mono text-[9px] tracking-[0.2em] text-sand/40 uppercase">{label}</p>
                <p className="mt-2 font-serif text-3xl font-semibold text-brass">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setTab(t)}
              className={`border px-4 py-2 font-mono text-[10px] tracking-[0.18em] uppercase transition-all ${
                tab === t ? "border-brass bg-brass/10 text-brass" : "border-bone/15 text-sand/50 hover:border-brass/40"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "Orders" && (
            <Panel title="Orders" testId="admin-orders-panel">
              {orders.length === 0 && <Empty>No orders yet.</Empty>}
              {orders.map((o) => (
                <div key={o.id} data-testid={`admin-order-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <p className="font-mono text-sm font-bold text-brass">{o.id}</p>
                    <p className="mt-0.5 text-xs text-sand/60">
                      {o.customer?.name} · {o.customer?.email} · {o.customer?.region}
                    </p>
                    <p className="mt-0.5 text-xs text-sand/45">
                      {o.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}
                    </p>
                    {(o.notes || []).map((n, idx) => (
                      <p key={idx} className="mt-1 text-[11px] italic text-sand/50">
                        ✎ {n.text}
                      </p>
                    ))}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <input
                        data-testid={`note-input-${o.id}`}
                        placeholder="Staff note…"
                        value={noteDrafts[o.id] ?? ""}
                        onChange={(e) => setNoteDrafts((d) => ({ ...d, [o.id]: e.target.value }))}
                        className="w-40 border border-brass/20 bg-forest-deep/70 px-2 py-1 font-mono text-[10px] text-bone outline-none focus:border-brass"
                      />
                      <button
                        data-testid={`note-save-${o.id}`}
                        onClick={() => saveNote(o.id)}
                        className="border border-brass/30 px-2 py-1 font-mono text-[9px] text-brass uppercase transition-all hover:bg-brass/10"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-bone">{fmtMoney(o.total, o.currency)}</p>
                    <p className={`mt-0.5 font-mono text-[10px] tracking-[0.15em] uppercase ${o.payment_status === "paid" ? "text-brass" : "text-sand/45"}`}>
                      {o.payment_method} · {o.payment_status}
                    </p>
                    <p className="mt-0.5 font-mono text-[9px] text-sand/35">{fmtDate(o.created_at)}</p>
                    <div className="mt-2 flex flex-col items-end gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`order-status-${o.id}`}
                          className={`border px-2 py-1 font-mono text-[9px] tracking-[0.15em] uppercase ${
                            o.status === "delivered"
                              ? "border-brass/60 bg-brass/15 text-brass"
                              : o.status === "dispatched"
                                ? "border-brass/40 bg-brass/5 text-brass/80"
                                : ["refunded", "cancelled"].includes(o.status)
                                  ? "border-[#E65100]/50 text-[#E65100]"
                                  : "border-bone/15 text-sand/50"
                          }`}
                        >
                          {(o.status || "").replace(/_/g, " ")}
                        </span>
                        {o.status === "confirmed" && (
                          <button
                            data-testid={`dispatch-order-${o.id}`}
                            onClick={() => {
                              setDispatchFor(dispatchFor === o.id ? null : o.id);
                              setTracking({ tracking_number: "", courier: "" });
                            }}
                            className="border border-brass/40 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                          >
                            Dispatch
                          </button>
                        )}
                        {o.status === "dispatched" && (
                          <button
                            data-testid={`deliver-order-${o.id}`}
                            onClick={() => advanceStatus(o.id, "delivered")}
                            className="border border-brass/40 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                          >
                            Mark Delivered
                          </button>
                        )}
                        {o.status === "awaiting_payment" && !o.reminder_sent && (
                          <button
                            data-testid={`nudge-order-${o.id}`}
                            onClick={() => nudgeOrder(o.id)}
                            className="border border-brass/40 px-2.5 py-1 font-mono text-[9px] font-bold tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                          >
                            Nudge
                          </button>
                        )}
                        {o.status === "awaiting_payment" && o.reminder_sent && (
                          <span className="font-mono text-[9px] tracking-[0.15em] text-sand/40 uppercase">
                            Nudged
                          </span>
                        )}
                        {["paid", "manual_pending"].includes(o.payment_status) &&
                          !["refunded", "cancelled"].includes(o.status) && (
                            <button
                              data-testid={`refund-order-${o.id}`}
                              onClick={() => refundOrder(o.id)}
                              className="border border-bone/20 px-2.5 py-1 font-mono text-[9px] tracking-[0.15em] text-sand/60 uppercase transition-all hover:border-[#E65100] hover:text-[#E65100]"
                            >
                              {o.payment_status === "paid" ? "Refund" : "Cancel"}
                            </button>
                          )}
                      </div>
                      {o.tracking_number && (
                        <p className="font-mono text-[9px] text-sand/45">
                          {o.courier || "Courier"} · {o.tracking_number}
                        </p>
                      )}
                      {dispatchFor === o.id && (
                        <div data-testid={`dispatch-form-${o.id}`} className="flex items-center gap-2">
                          <input
                            data-testid={`tracking-input-${o.id}`}
                            placeholder="Tracking no."
                            value={tracking.tracking_number}
                            onChange={(e) => setTracking((t) => ({ ...t, tracking_number: e.target.value }))}
                            className="w-32 border border-brass/25 bg-forest-deep/70 px-2 py-1.5 font-mono text-[10px] text-bone outline-none focus:border-brass"
                          />
                          <input
                            data-testid={`courier-input-${o.id}`}
                            placeholder="Courier"
                            value={tracking.courier}
                            onChange={(e) => setTracking((t) => ({ ...t, courier: e.target.value }))}
                            className="w-24 border border-brass/25 bg-forest-deep/70 px-2 py-1.5 font-mono text-[10px] text-bone outline-none focus:border-brass"
                          />
                          <button
                            data-testid={`confirm-dispatch-${o.id}`}
                            onClick={() => advanceStatus(o.id, "dispatched", tracking)}
                            className="bg-brass px-2.5 py-1.5 font-mono text-[9px] font-bold tracking-[0.15em] text-forest-deep uppercase hover:bg-brass-bright"
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Enquiries" && (
            <Panel title="Enquiries" testId="admin-enquiries-panel">
              {enquiries.length === 0 && <Empty>No enquiries yet.</Empty>}
              {enquiries.map((e) => (
                <div key={e.id} className="px-5 py-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="text-sm text-bone">{e.name} <span className="text-sand/45">· {e.email} · {e.country}</span></p>
                    <p className="font-mono text-[9px] text-sand/35">{fmtDate(e.created_at)}</p>
                  </div>
                  {e.model_interest && <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-brass uppercase">{e.model_interest}</p>}
                  <p className="mt-1.5 text-sm font-light text-sand/65">{e.message}</p>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Price Matches" && (
            <Panel title="Price Match Requests" testId="admin-price-matches-panel">
              {priceMatches.length === 0 && <Empty>No price match requests yet.</Empty>}
              {priceMatches.map((p) => (
                <div key={p.id} className="px-5 py-4">
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="text-sm text-bone">{p.product_name} <span className="text-sand/45">· {p.name} · {p.email}</span></p>
                    <p className="font-mono text-[9px] text-sand/35">{fmtDate(p.created_at)}</p>
                  </div>
                  <p className="mt-1 text-xs text-sand/60">
                    Competitor: <span className="font-mono text-brass">{p.competitor_url}</span> at{" "}
                    {fmtMoney(p.competitor_price, p.currency)}
                  </p>
                  {p.notes && <p className="mt-1 text-sm font-light text-sand/55">{p.notes}</p>}
                  <div className="mt-3 flex items-center gap-2">
                    {p.status === "pending_review" ? (
                      <>
                        <button
                          data-testid={`approve-match-${p.id}`}
                          onClick={() => decidePriceMatch(p.id, "approved")}
                          className="border border-brass/40 px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                        >
                          Approve + Email
                        </button>
                        <button
                          data-testid={`decline-match-${p.id}`}
                          onClick={() => decidePriceMatch(p.id, "declined")}
                          className="border border-bone/20 px-3 py-1.5 font-mono text-[9px] tracking-[0.15em] text-sand/60 uppercase transition-all hover:border-[#E65100] hover:text-[#E65100]"
                        >
                          Decline + Email
                        </button>
                      </>
                    ) : (
                      <span
                        data-testid={`match-status-${p.id}`}
                        className={`font-mono text-[9px] tracking-[0.18em] uppercase ${p.status === "approved" ? "text-brass" : "text-[#E65100]"}`}
                      >
                        {p.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Waitlist" && (
            <Panel title="Waitlist Radar — who's waiting for what" testId="admin-waitlist-panel">
              {waitlist.length === 0 && <Empty>Nobody waiting — every model in stock.</Empty>}
              {Object.entries(
                waitlist.reduce((acc, w) => {
                  (acc[w.product_name] = acc[w.product_name] || []).push(w);
                  return acc;
                }, {})
              ).map(([model, entries]) => (
                <div key={model} className="px-5 py-4">
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-lg font-semibold text-bone">{model}</p>
                    <span className="border border-brass/40 bg-brass/10 px-2.5 py-1 font-mono text-[9px] tracking-[0.15em] text-brass uppercase">
                      {entries.length} waiting
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {entries.map((w) => (
                      <li key={w.email} className="flex flex-wrap justify-between gap-2 text-sm">
                        <span className="font-mono text-xs text-sand/70">{w.email}</span>
                        <span className="font-mono text-[9px] text-sand/35">{fmtDate(w.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Go Live" && (
            <Panel title="Go-Live Checklist — walk these in order" testId="admin-golive-panel">
              {GO_LIVE_STEPS.map((s, i) => (
                <div key={s.key} className="flex items-start gap-4 px-5 py-4">
                  <input
                    type="checkbox"
                    data-testid={`golive-check-${i + 1}`}
                    checked={!!liveChecks[s.key]}
                    onChange={() =>
                      setLiveChecks((c) => {
                        const n = { ...c, [s.key]: !c[s.key] };
                        localStorage.setItem("xs-golive", JSON.stringify(n));
                        return n;
                      })
                    }
                    className="mt-1.5 h-4 w-4 shrink-0 accent-[#C5A059]"
                  />
                  <div>
                    <p className={`font-serif text-lg font-semibold ${liveChecks[s.key] ? "text-sand/40 line-through" : "text-bone"}`}>
                      {i + 1}. {s.title}
                    </p>
                    <p className="mt-1 max-w-2xl text-sm font-light leading-relaxed text-sand/60">{s.body}</p>
                    {s.href && (
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`golive-link-${i + 1}`}
                        className="mt-2.5 inline-block border border-brass/40 px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                      >
                        {s.cta}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Exports" && (
            <Panel title="Spreadsheet exports (CSV)" testId="admin-exports-panel">
              {[
                ["orders", "Orders — the full order book with status, courier and tracking"],
                ["waitlist", "Waitlist — riders waiting, per model"],
                ["enquiries", "Enquiries — every contact form message"],
                ["newsletter", "Newsletter members — email signups"],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between gap-3 px-5 py-4">
                  <p className="text-sm font-light text-sand/70">{label}</p>
                  <button
                    data-testid={`export-${key}-button`}
                    onClick={() => exportCsv(key)}
                    className="bg-brass px-4 py-2 font-mono text-[9px] font-bold tracking-[0.15em] text-forest-deep uppercase transition-colors hover:bg-brass-bright"
                  >
                    Download CSV
                  </button>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Inventory" && (
            <Panel title="Inventory — restocking from zero emails the waitlist automatically" testId="admin-inventory-panel">
              {products.map((p) => (
                <div key={p.id} data-testid={`inventory-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <p className="text-sm text-bone">{p.name}</p>
                    <p className="mt-0.5 font-mono text-[10px] tracking-[0.15em] text-sand/45 uppercase">
                      {p.category}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[10px] tracking-[0.15em] uppercase ${p.stock <= 0 ? "font-bold text-[#E65100]" : "text-sand/50"}`}>
                      {p.stock} in stock
                    </span>
                    <input
                      data-testid={`stock-input-${p.id}`}
                      type="number"
                      min="0"
                      placeholder="New stock"
                      value={stockEdits[p.id] ?? ""}
                      onChange={(e) => setStockEdits((s) => ({ ...s, [p.id]: e.target.value }))}
                      className="w-24 border border-brass/25 bg-forest-deep/70 px-2 py-1.5 font-mono text-xs text-bone outline-none focus:border-brass"
                    />
                    <button
                      data-testid={`stock-save-${p.id}`}
                      onClick={() => saveStock(p.id)}
                      className="bg-brass px-3 py-1.5 font-mono text-[9px] font-bold tracking-[0.15em] text-forest-deep uppercase transition-colors hover:bg-brass-bright"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ))}
            </Panel>
          )}

          {tab === "Rider Stories" && (
            <Panel title="Rider Stories" testId="admin-stories-panel">
              {stories.length === 0 && <Empty>No stories yet.</Empty>}
              {stories.map((s) => (
                <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div className="flex items-center gap-4">
                    {s.image && (
                      <img src={imageUrl(s.image)} alt="" className="h-14 w-14 shrink-0 border border-brass/20 object-cover" />
                    )}
                    <div>
                      <p className="text-sm text-bone">{s.name} <span className="text-sand/45">· {s.location} · {s.bike}</span></p>
                      <p className="mt-1 max-w-xl text-sm font-light italic text-sand/60">"{s.quote}"</p>
                    </div>
                  </div>
                  {s.status === "pending" ? (
                    <button
                      data-testid={`approve-story-${s.id}`}
                      onClick={() => approve(s.id)}
                      className="flex items-center gap-2 border border-brass/40 px-3 py-2 font-mono text-[10px] tracking-[0.15em] text-brass uppercase transition-all hover:bg-brass/10"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Approve
                    </button>
                  ) : (
                    <span className="font-mono text-[10px] tracking-[0.15em] text-brass/70 uppercase">Approved</span>
                  )}
                </div>
              ))}
            </Panel>
          )}
        </div>
      </main>
    </div>
  );
}
